import classNames from "classnames";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { randomIntFromInterval } from "src/helpers/helpers";
import { ArrowDownIcon } from "src/icons/ArrowDownIcon";
import StoreIconV2 from "src/icons/StoreIconV2";
import TickIcon from "src/icons/TickIcon";
import { setActiveStore } from "src/redux/actions/app";
import { getActiveStoreId, getUserStoresData } from "src/redux/selectors/app";
import Button from "../buttons/Button";
import ContextDropdown from "../dropdowns/ContextDropdown";

const colors = [
  {
    textColor: "#8280FF",
    bgColor: "bg-[#8280ff36]",
  },
  {
    textColor: "#00BAD1",
    bgColor: "bg-[#00BAD136]",
  },
  {
    textColor: "#FF4C51",
    bgColor: "bg-[#FF4C5136]",
  },
];

type store = {
  id: number;
  name: string;
  email: string;
  gst: string;
  phone: string | null;
  alternatePhone: string | null;
  fax: string;
  address: string;
  country: string;
  state: string;
  city: string;
  status: true;
  role: number;
};

const StoreSelector = ({ className }: { className?: string }) => {
  const userStoresData = useSelector(getUserStoresData);
  const activeStoreId = useSelector(getActiveStoreId);
  const randomColors = useMemo(() => colors[randomIntFromInterval(0, 2)], []);

  if (userStoresData.length < 1) {
    return null;
  }

  const { name, address } =
    userStoresData.find((sd: store) => activeStoreId === sd.id) ?? {};

  return (
    <ContextDropdown
      content={<StoreDropdown />}
      placement={"bottom"}
      className="w-full"
    >
      <div
        className={classNames(
          "p-[5px] border-lightGrey border border-solid rounded-lg cursor-pointer select-none w-[177px] relative flex items-center gap-2",
          className
        )}
      >
        <div className={`px-[7px] py-2 rounded-full ${randomColors.bgColor}`}>
          <StoreIconV2 color={randomColors.textColor} />
        </div>
        <div>
          <div className="text-[#282A2D] text-base font-medium truncate max-w-[94px]">
            {name}
          </div>
          <div className="text-[#788087] text-xs font-light leading-[18px] truncate max-w-[94px]">
            {address}
          </div>
        </div>

        <ArrowDownIcon className="absolute right-2.5" />
      </div>
    </ContextDropdown>
  );
};

export default StoreSelector;

const StoreDropdown = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userStoresData = useSelector(getUserStoresData);
  const activeStoreId = useSelector(getActiveStoreId);

  const lastIndex = userStoresData.length - 1;

  return (
    <div className="flex flex-col">
      <div className="flex flex-col py-2 rounded-14px w-[343px] max-h-[320px] overflow-y-scroll scrollbar-thin">
        {userStoresData.map(({ name, id, address }: store, i: number) => {
          const randomColors = colors[i % 3];
          return (
            <div
              key={`store-${id}`}
              className={classNames(
                "flex items-center py-[9px] px-4 gap-2 cursor-pointer hover:bg-white-ghost text-14px font-semibold relative",
                { "border-b": i !== lastIndex }
              )}
              onClick={() => dispatch(setActiveStore(id))}
            >
              <div
                className={`px-[7px] py-2 rounded-full w-[34px] h-[34px] ${randomColors.bgColor}]`}
              >
                <StoreIconV2 color={randomColors.textColor} />
              </div>
              <div className="flex flex-col text-gray-tundora font-semibold leading-normal w-full">
                <span className="whitespace-nowrap text-14px text-ellipsis max-w-[78%] truncate">
                  {name}
                </span>
                <span className="text-[12px] max-w-[78%] truncate">
                  {address}
                </span>
              </div>
              <div className="absolute right-4">
                {id === activeStoreId && <TickIcon />}
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-3">
        <Button
          text={"Create Store"}
          className="w-full"
          primary
          onClick={() => navigate("/create-store")}
        />
      </div>
    </div>
  );
};
